import React, { useEffect, useRef, useState } from 'react';
import './Styles.css';
import '@ids/style-modal-v19';
import { IconButton, IconButtonSize, IconButtonType, IconButtonVariant } from '@ids/react-icon-button';
import { Ui16Cross } from '@ids/react-icons';

const DraggableModal = ({ children, closeView, titleText, buttons }) => {
  const dialogRef = useRef(null);
  const dragDataRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialLeft: 0,
    initialTop: 0,
  });

  const [position, setPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!dialog.open) {
      dialog.showModal();
    }

    requestAnimationFrame(() => {
      const rect = dialog.getBoundingClientRect();

      setPosition({
        left: Math.max((window.innerWidth - rect.width) / 2, 16),
        top: Math.max((window.innerHeight - rect.height) / 2, 16),
      });
    });
  }, []);

  useEffect(() => {
    const handlePointerMove = e => {
      if (!dragDataRef.current.isDragging) return;

      const deltaX = e.clientX - dragDataRef.current.startX;
      const deltaY = e.clientY - dragDataRef.current.startY;

      const dialog = dialogRef.current;
      if (!dialog) return;

      const rect = dialog.getBoundingClientRect();

      let nextLeft = dragDataRef.current.initialLeft + deltaX;
      let nextTop = dragDataRef.current.initialTop + deltaY;

      const minVisible = 40;
      const maxLeft = window.innerWidth - minVisible;
      const maxTop = window.innerHeight - minVisible;
      const minLeft = -(rect.width - minVisible);
      const minTop = 0;

      nextLeft = Math.min(Math.max(nextLeft, minLeft), maxLeft);
      nextTop = Math.min(Math.max(nextTop, minTop), maxTop);

      setPosition({
        left: nextLeft,
        top: nextTop,
      });
    };

    const handlePointerUp = () => {
      dragDataRef.current.isDragging = false;
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.userSelect = '';
    };
  }, []);

  const startDrag = e => {
    if (e.target.closest('button')) return;

    dragDataRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      initialLeft: position.left,
      initialTop: position.top,
    };

    document.body.style.userSelect = 'none';
  };

  return (
    <dialog
      ref={dialogRef}
      className="draggable-modal"
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}>
      <div className="draggableModal-titleContainer draggable-modal-header" onPointerDown={startDrag}>
        <h2 className="draggableModal-title">{titleText}</h2>

        <IconButton
          type={IconButtonType.PLAIN}
          variant={IconButtonVariant.QUIET}
          size={IconButtonSize.SMALL}
          onClick={closeView}>
          <Ui16Cross />
        </IconButton>
      </div>

      {children}

      <div className="draggableModal-buttonContainer">{buttons}</div>
    </dialog>
  );
};

export default DraggableModal;
